import { CheckCircleOutlined, CircleOutlined, PlayArrowOutlined, RefreshOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, CircularProgress, Grid, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import { getMemberHealthCheckCategoryCount, getMemberHealthCheckSummary, HealthCheckCategoryResponse } from 'src/shared/services/healthCheckService';

// Stabil ordning på kategorierna i UI. När backend får en ny check läggs nyckeln till här
// och i sv/en troubleshooting.json. Hellre explicit än dynamisk discovery — sparar ett
// SQL-anrop varje gång sidan laddas, vilket är poängen med opt-in-körning.
const CATEGORY_KEYS = ['missing-expire-date', 'invalid-person-nr', 'invalid-email', 'invalid-geo-code', 'duplicate-members', 'invalid-household'];

type CategoryStatus = 'idle' | 'loading' | 'done' | 'error';

type CategoryState = {
  status: CategoryStatus;
  count?: number;
};

export const StatisticsTroubleshootingPage = () => {
  const { t } = useTranslation(['troubleshooting']);
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const associationId = searchParams.get('associationId') ? Number(searchParams.get('associationId')) : undefined;
  const districtId = searchParams.get('districtId') ? Number(searchParams.get('districtId')) : undefined;

  const [association, setAssociation] = useState<Association>();
  const [district, setDistrict] = useState<District>();

  // Per-kategori-state — initialt alla idle. Vi kör INGEN check automatiskt vid mount eftersom
  // checks kan vara tunga (några hundra ms till flera sekunder vid stora datamängder).
  const [states, setStates] = useState<Record<string, CategoryState>>(() =>
    CATEGORY_KEYS.reduce((acc, key) => ({ ...acc, [key]: { status: 'idle' as CategoryStatus } }), {}),
  );

  // Förvälj scope om användaren är begränsad — samma mönster som dashboarden.
  useEffect(() => {
    if (!currentUser || association || district || associationId || districtId) return;
    if (currentUser.districts.length === 0 && currentUser.associations.length === 1) {
      setAssociation(currentUser.associations[0] as unknown as Association);
    } else if (currentUser.associations.length === 0 && currentUser.districts.length === 1) {
      setDistrict(currentUser.districts[0] as unknown as District);
    }
  }, [currentUser, association, district, associationId, districtId]);

  // Synka URL när scopet ändras + nollställ alla resultat (gamla siffror gäller inte längre).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (association?.localAss_id) next.set('associationId', String(association.localAss_id));
    else next.delete('associationId');
    if (district?.countyAss_id) next.set('districtId', String(district.countyAss_id));
    else next.delete('districtId');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
      setStates(CATEGORY_KEYS.reduce((acc, key) => ({ ...acc, [key]: { status: 'idle' as CategoryStatus } }), {}));
    }
  }, [association, district, searchParams, setSearchParams]);

  const runAll = useCallback(() => {
    setStates((prev) =>
      CATEGORY_KEYS.reduce((acc, key) => ({ ...acc, [key]: { status: 'loading' as CategoryStatus, count: prev[key]?.count } }), {}),
    );
    getMemberHealthCheckSummary(associationId, districtId)
      .then((res) => {
        if (!isMounted()) return;
        const byKey = new Map(res.data.map((c: HealthCheckCategoryResponse) => [c.key, c]));
        setStates(
          CATEGORY_KEYS.reduce((acc, key) => {
            const entry = byKey.get(key);
            return { ...acc, [key]: entry ? { status: 'done' as CategoryStatus, count: entry.count } : { status: 'idle' as CategoryStatus } };
          }, {}),
        );
      })
      .catch((e) => {
        ConsoleHelper.error(e);
        if (!isMounted()) return;
        setStates((prev) =>
          CATEGORY_KEYS.reduce(
            (acc, key) => ({ ...acc, [key]: prev[key].status === 'loading' ? { status: 'error' as CategoryStatus } : prev[key] }),
            {},
          ),
        );
      });
  }, [isMounted, associationId, districtId]);

  const runSingle = useCallback(
    (key: string) => {
      setStates((prev) => ({ ...prev, [key]: { status: 'loading', count: prev[key]?.count } }));
      getMemberHealthCheckCategoryCount(key, associationId, districtId)
        .then((res) => {
          if (!isMounted()) return;
          setStates((prev) => ({ ...prev, [key]: { status: 'done', count: res.data.count } }));
        })
        .catch((e) => {
          ConsoleHelper.error(e);
          if (!isMounted()) return;
          setStates((prev) => ({ ...prev, [key]: { status: 'error' } }));
        });
    },
    [isMounted, associationId, districtId],
  );

  const navigateToCategory = (categoryKey: string) => {
    const params = new URLSearchParams();
    if (associationId) params.set('associationId', String(associationId));
    if (districtId) params.set('districtId', String(districtId));
    const query = params.toString();
    navigate(`/statistics/troubleshooting/${categoryKey}${query ? `?${query}` : ''}`);
  };

  const renderStatusIcon = (state: CategoryState) => {
    if (state.status === 'loading') return <CircularProgress size={20} />;
    if (state.status === 'error') return <WarningAmberOutlined sx={{ color: 'error.main', fontSize: 22 }} />;
    if (state.status === 'idle') return <CircleOutlined sx={{ color: 'text.disabled', fontSize: 22 }} />;
    if (state.count === 0) return <CheckCircleOutlined sx={{ color: 'success.main', fontSize: 22 }} />;
    return <WarningAmberOutlined sx={{ color: 'warning.main', fontSize: 22 }} />;
  };

  const borderColorForState = (state: CategoryState): string => {
    if (state.status === 'done' && state.count === 0) return theme.palette.success.main;
    if (state.status === 'done' && (state.count ?? 0) > 0) return theme.palette.warning.main;
    if (state.status === 'error') return theme.palette.error.main;
    return theme.palette.divider;
  };

  const anyRunning = Object.values(states).some((s) => s.status === 'loading');

  return (
    <GridWrapper>
      <Grid item xs={12}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'flex-end' }} justifyContent='space-between' spacing={2}>
          <MainContentHeader title={t('troubleshooting:header')} />
          {currentUser && (
            <Box sx={{ display: 'flex', gap: 1.5, '& .MuiAutocomplete-inputRoot': { backgroundColor: 'white' } }}>
              <AssociationAndDistrictSelect
                hideDistrict={currentUser.districts.length === 0}
                association={association}
                district={district}
                associationCallback={(val) => setAssociation(val || undefined)}
                districtCallback={(val) => setDistrict(val || undefined)}
              />
            </Box>
          )}
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Alert
            severity='info'
            sx={{ mb: 3, '& .MuiAlert-action': { alignItems: 'center', pt: 0, pr: 1 } }}
            action={
              <Button
                variant='contained'
                color='primary'
                startIcon={<PlayArrowOutlined />}
                onClick={runAll}
                disabled={anyRunning}
                disableElevation
                sx={{ whiteSpace: 'nowrap' }}
              >
                {anyRunning ? t('troubleshooting:running') : t('troubleshooting:runCheck')}
              </Button>
            }
          >
            <AlertTitle>{t('troubleshooting:alertTitle')}</AlertTitle>
            {t('troubleshooting:subtitle')}
          </Alert>

          <Stack spacing={1}>
            {CATEGORY_KEYS.map((key) => {
              const state = states[key];
              const isClickable = state.status === 'done';
              const rowContent = (
                <Stack direction='row' alignItems='center' spacing={2} sx={{ width: '100%', py: 1.5, px: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 36 }}>{renderStatusIcon(state)}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='body1' sx={{ fontWeight: 500 }}>
                      {t(`troubleshooting:categories.${key}.header`)}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {t(`troubleshooting:categories.${key}.description`)}
                    </Typography>
                  </Box>
                  <Stack direction='row' alignItems='center' spacing={1.5}>
                    {state.status === 'done' && (
                      <Typography
                        variant='body2'
                        sx={{
                          fontWeight: 600,
                          color: (state.count ?? 0) > 0 ? 'warning.dark' : 'text.disabled',
                          minWidth: 72,
                          textAlign: 'right',
                        }}
                      >
                        {t('troubleshooting:issuesCount', { count: state.count ?? 0 })}
                      </Typography>
                    )}
                    {(() => {
                      if (state.status === 'idle' || state.status === 'error') {
                        return (
                          <Button
                            size='small'
                            variant='outlined'
                            startIcon={<PlayArrowOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              runSingle(key);
                            }}
                            disableElevation
                          >
                            {t('troubleshooting:runSingleButton')}
                          </Button>
                        );
                      }
                      if (state.status === 'loading') {
                        return (
                          <Typography variant='body2' color='text.secondary' sx={{ minWidth: 100, textAlign: 'right' }}>
                            {t('troubleshooting:running')}
                          </Typography>
                        );
                      }
                      return (
                        <Tooltip arrow title={t('troubleshooting:runSingle')}>
                          <IconButton
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation();
                              runSingle(key);
                            }}
                          >
                            <RefreshOutlined fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      );
                    })()}
                    {isClickable && (
                      <Button
                        size='small'
                        variant='contained'
                        color='primary'
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateToCategory(key);
                        }}
                        disableElevation
                      >
                        {t('troubleshooting:actions.review')}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              );

              return (
                <Box
                  key={key}
                  sx={{
                    border: '1px solid',
                    borderColor: borderColorForState(state),
                    borderRadius: 1,
                    transition: 'border-color 0.2s, background-color 0.15s',
                    overflow: 'hidden',
                    ...(isClickable
                      ? {
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                        }
                      : {}),
                  }}
                  onClick={isClickable ? () => navigateToCategory(key) : undefined}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      navigateToCategory(key);
                    }
                  }}
                >
                  {rowContent}
                </Box>
              );
            })}
          </Stack>
        </Paper>
      </Grid>
    </GridWrapper>
  );
};
